// apps/web/src/pages/author/widget-config/branch-variant-config.tsx
//
// Phase 8 config sub-form for template `branch-variant` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/branch-variant.ts AND the
// per-template walker at packages/shared/src/forms/per-template/branch-variant.ts
// (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. Every useFieldArray map uses field.id
// keys per authr-03 (Plan 01 grep gate enforces).
//
// configSchema shape (verbatim, from branch-variant.ts):
//   sourceOptionSetWidgetId: string
//   variants: ReadonlyArray<{ id; label; attachToOptionIds[]; overrideFields }>
//
// The PLAN description references "overrideField" (singular) but the canonical
// schema field is `overrideFields` (record). We mirror the schema — single
// source of truth (mirrors IngredientGroups W13 reconciliation pattern). We
// render `overrideFields` as a textarea (JSON) for Phase 8 v1; Phase 6 will
// generalize per-variant override editing if needed.
//
// W11 — exports `defaultBranchVariantConfig(): BranchVariantConfig` factory.

import type { BranchVariantConfig } from "@the-recipe-spread/shared";
import { useState } from "react";
import { type ControllerRenderProps, useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

export interface BranchVariantConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// CR-01 fix — OverrideFieldsTextarea owns a LOCAL raw-text buffer and only
// commits a parsed value into form state on a valid JSON.parse. The previous
// implementation called `f.onChange(raw)` on parse failure, writing a STRING
// into a field whose typed shape is `Readonly<Record<string, unknown>>` —
// downstream `JSON.stringify(f.value, ...)` would then render the string
// wrapped in quotes on the next render, and the safeParse in PreviewPane +
// the Pass 2A validateRecipe at save time would both reject the corrupted
// shape. Now the typed invariant holds: form state is ALWAYS a record (or
// the last-good record), even while the user types invalid JSON.
function OverrideFieldsTextarea(props: {
  field: ControllerRenderProps;
}): React.ReactElement {
  const { field: f } = props;
  // Initialize the local raw-text buffer from the form-state value. This
  // re-runs on remount (e.g., when the parent FieldArrayRow is added or the
  // row index shifts after a `move`), but NOT on every parent re-render —
  // useState's initializer runs once per mount.
  const [rawText, setRawText] = useState<string>(() => {
    try {
      return f.value === undefined || f.value === null
        ? "{}"
        : JSON.stringify(f.value, null, 2);
    } catch {
      return "{}";
    }
  });
  return (
    <Textarea
      value={rawText}
      onChange={(e) => {
        const next = e.target.value;
        setRawText(next);
        try {
          const parsed = next.trim() === "" ? {} : JSON.parse(next);
          f.onChange(parsed);
        } catch {
          // Invalid JSON — KEEP the last good parsed value in form state.
          // The local rawText buffer shows what the user typed; the form-
          // state value stays a record so the typed invariant holds. The
          // FormMessage / save-time Pass 2A surface the validation error
          // when the user tries to save.
        }
      }}
      onBlur={f.onBlur}
      name={f.name}
      ref={f.ref}
    />
  );
}

// W11 — minimum-valid BranchVariantConfig per the configSchema:
//   variants.min(1); each variant: id, label, attachToOptionIds.min(1),
//   overrideFields (record, empty {} valid).
export function defaultBranchVariantConfig(): BranchVariantConfig {
  return {
    sourceOptionSetWidgetId: "",
    variants: [
      {
        id: "variant-1",
        label: "Variant 1",
        attachToOptionIds: [""],
        overrideFields: {},
      },
    ],
  };
}

export function BranchVariantConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: BranchVariantConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `${basePath}.variants`,
  });

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={control}
        name={`${basePath}.sourceOptionSetWidgetId`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.sourceOptionSetWidgetId`}>
            <FormLabel>Source option-set widget id</FormLabel>
            <FormControl>
              <Input {...f} value={f.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Fieldset legend="VARIANTS">
        {fields.length === 0 ? (
          <EmptyState
            title="No variants yet."
            cta={{
              label: "Add variant",
              onClick: () =>
                append({
                  id: `variant-${fields.length + 1}`,
                  label: `Variant ${fields.length + 1}`,
                  attachToOptionIds: [""],
                  overrideFields: {},
                }),
            }}
          />
        ) : (
          <>
            {fields.map((field, index) => (
              <FieldArrayRow
                key={field.id}
                singular="variant"
                index={index}
                canMoveUp={index > 0}
                canMoveDown={index < fields.length - 1}
                onMoveUp={() => move(index, index - 1)}
                onMoveDown={() => move(index, index + 1)}
                onRemove={() => remove(index)}
                deleteDialogTitle="Delete variant?"
                deleteDialogBody="This variant will be removed from the branch."
                deleteConfirmLabel="Delete variant"
              >
                <FormField
                  control={control}
                  name={`${basePath}.variants.${index}.id`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.variants.${index}.id`}>
                      <FormLabel>Id (kebab-case)</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.variants.${index}.label`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.variants.${index}.label`}>
                      <FormLabel>Label</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.variants.${index}.attachToOptionIds`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.variants.${index}.attachToOptionIds`}>
                      <FormLabel>Attach to option ids (comma-separated)</FormLabel>
                      <FormControl>
                        <Textarea
                          value={Array.isArray(f.value) ? (f.value as string[]).join(", ") : ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parts = raw
                              .split(",")
                              .map((s) => s.trim())
                              .filter((s) => s.length > 0);
                            f.onChange(parts.length === 0 ? [""] : parts);
                          }}
                          onBlur={f.onBlur}
                          name={f.name}
                          ref={f.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.variants.${index}.overrideFields`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.variants.${index}.overrideFields`}>
                      <FormLabel>Override fields (JSON object)</FormLabel>
                      <FormControl>
                        <OverrideFieldsTextarea field={f} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FieldArrayRow>
            ))}
            <button
              type="button"
              onClick={() =>
                append({
                  id: `variant-${fields.length + 1}`,
                  label: `Variant ${fields.length + 1}`,
                  attachToOptionIds: [""],
                  overrideFields: {},
                })
              }
              className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Add variant
            </button>
          </>
        )}
      </Fieldset>
    </div>
  );
}
BranchVariantConfigSubForm.displayName = "BranchVariantConfigSubForm";
